export class RESTAPIError extends Error {
  name = 'RESTAPIError';
  status: number;
  url: string;
  code: string;
  message: string;
  stack?: string;

  constructor({
    status,
    url,
    code,
    message,
    cause,
    stack,
  }: {
    status: number;
    url: string;
    code: string;
    message: string;
    cause?: unknown;
    stack?: string;
  }) {
    super(message);
    this.status = status;
    this.url = url;
    this.code = code;
    this.message = message;
    this.cause = cause;
    if (stack) {
      this.stack = stack || undefined;
    }
  }

  toJSON() {
    return { status: this.status, url: this.url, code: this.code, message: this.message, stack: this.stack };
  }
}

export function isRESTAPIError(error: unknown): error is RESTAPIError {
  return error instanceof RESTAPIError;
}

/** True when `error` is a REST API error carrying the given HTTP status code. */
export function isRESTAPIErrorWithStatus(error: unknown, status: number): boolean {
  return isRESTAPIError(error) && error.status === status;
}

/** True when `error` is a 409 Conflict from the REST API (e.g. the resource already exists). */
export function isConflictError(error: unknown): boolean {
  return isRESTAPIErrorWithStatus(error, 409);
}

/**
 * Extract a human-readable message from an unknown thrown value. Covers `RESTAPIError`,
 * `NetworkError`, and any other `Error`; anything else falls back to `fallback`.
 */
export function getErrorMessage(error: unknown, fallback = 'An unknown error occurred.'): string {
  return error instanceof Error ? error.message : fallback;
}

export class NetworkError extends Error {
  name = 'NetworkError';
  message: string;
  stack?: string;

  constructor({ message, cause, stack }: { message: string; cause?: unknown; stack?: string }) {
    super(message);
    this.message = message;
    this.cause = cause;
    if (stack) {
      this.stack = stack || undefined;
    }
  }

  toJSON() {
    return { message: this.message, stack: this.stack };
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
